import React, { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import FieldContainer from '@common/FieldContainer';
import { useToast } from '@common/toast';
import { useDuplicateStudentIdCheck, useUpdateAffiliatedUniversity } from '@repository/partnership/usePartnership';
import CertRemoveButton from '@components/mypage/premium/CertRemoveButton';
import Major from './Major';
import StudentId from './StudentId';
import { AffiliatedUniversityInfoUpdateVO } from '@domain/vo/alliance/AffiliatedUniversityInfoVO';
import { AllianceInfoVO } from '@domain/vo/alliance/AllianceInfoVO';
import { KeyboardEventUtil } from '@utils/KeyboardEventUtil';

import classnames from 'classnames/bind';
import styles from '../premium.module.scss';

const cx = classnames.bind(styles);
interface CompletedFormProps {
  data: AllianceInfoVO;
  refetchData: () => void;
}

const CompletedForm: FC<CompletedFormProps> = ({ data, refetchData }) => {
  const Toast = useToast();
  const { mutateAsync: updateAffiliatedUniversity } = useUpdateAffiliatedUniversity();
  const { mutateAsync: checkDuplicateStudentId } = useDuplicateStudentIdCheck();

  const methods = useForm<AffiliatedUniversityInfoUpdateVO>({
    mode: 'onChange',
  });
  const {
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    setError,
    getValues,
    formState: { isDirty },
  } = methods;

  const isDisabledSubmitBtn = !(Object.keys(errors).length < 1 && watch('major') && watch('studentId')) || !isDirty;

  //학번 중복 체크
  const onCheckDuplicateStudentId = async () => {
    if (data.universityInfo === null) return true;
    try {
      const result = await checkDuplicateStudentId({
        schoolCode: data.universityInfo.schoolCode,
        studentId: getValues('studentId'),
      });

      if (result.isDuplicated) setError('studentId', { message: '중복된 학번 정보입니다.' });
      return result.isDuplicated;
    } catch (e: any) {
      switch (e?.response?.data.errorCode) {
        case 'B901':
          Toast({ type: 'error', content: '다시 시도해주세요' });
          break;
        case 'B902':
          setError('studentId', { message: '올바른 학번 형식이 아닙니다.' });
          break;
        case 'B904':
          setError('studentId', { message: '중복된 학번 정보입니다.' });
          break;
      }

      return true;
    }
  };

  const onSubmit = async (data: AffiliatedUniversityInfoUpdateVO) => {
    //학번 값 확인(사용 가능한 학번 일 경우 true)
    const isError = await onCheckDuplicateStudentId();
    if (isError) return;

    const result = await updateAffiliatedUniversity(data);

    if (result) {
      Toast({ type: 'success', iconType: 'check', content: '변경사항이 저장되었습니다.' });
      refetchData();
    } else {
      Toast({ type: 'error', iconType: 'info', content: '변경사항 저장이 실패되었습니다.' });
    }
  };

  useEffect(() => {
    if (data.universityInfo) {
      reset({ ...data.universityInfo });
    }
  }, [data.universityInfo]);

  if (!data.universityInfo) return null;

  return (
    <div className={cx('contentsWrap')}>
      <div className={cx('requireTextArea')}>
        <span className={cx('requireText')}>필수 입력</span>
      </div>
      <FormProvider {...methods}>
        <form
          autoComplete={'off'}
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={KeyboardEventUtil.onCheckKeyDownPreventEnter}
        >
          <FieldContainer name="인증 방법">
            <div className={cx('inputArea')}>
              <input
                type="text"
                className={cx('input')}
                placeholder={`제휴 대학 이메일 인증 (${data.universityInfo.verifiedDate})`}
                disabled={true}
              />
            </div>
          </FieldContainer>
          <FieldContainer name="제휴 대학">
            <div className={cx('inputArea')}>
              <input type="text" className={cx('input')} placeholder={data.universityInfo.schoolName} disabled={true} />
            </div>
          </FieldContainer>
          <Major name={'major'} />
          <StudentId name={'studentId'} />
          <div className={cx('btnWrap')}>
            <button className={cx('btnSave')} type={'submit'} disabled={isDisabledSubmitBtn}>
              변경사항 저장
            </button>
            <CertRemoveButton onClick={refetchData} partnershipType={'UNIVERSITY'} />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default CompletedForm;
